import AccessServiceTypes from 'app/domain/AccessServiceTypes';
import { ContentItemFragment, Presentation } from 'app/graphql/__generated__/types/contento';
import ContinueWatching from 'app/utils/models/ContinueWatching';
import { OnItemImpressionType } from 'app/utils/useTrackImpression';
import React, { useState } from 'react';
import ContentItemCarousel from '../ContentItemCarousel/ContentItemCarousel';
import ContentItemGrid from '../ContentItemGrid/ContentItemGrid';
import Header from './components/Header/Header';
import { StyledContentList, VideoList, VideoListContainer } from './ContentList.styles';

export enum CardType {
    CONTENT_ITEM = 'CONTENT_ITEM',
    SHOWCASE = 'SHOWCASE',
}

export type OnItemClickType = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    itemPosition: number,
    svtId: string,
    autoPlay?: boolean,
    b?: boolean
) => void;

type Props = {
    items: Array<ContentItemFragment>;
    location?: string;
    id?: string;
    presentation?: Presentation;
    cardType?: CardType;
    className?: string;
    preferredAccessVersion?: AccessServiceTypes;
    onItemClick?: OnItemClickType;
    onItemImpression?: OnItemImpressionType;
    continueWatchingIsActive?: boolean;
    isInSweden?: boolean;
    autoPlay?: boolean;
    withDivider?: boolean;
    standalone?: boolean;
    vtp?: boolean;
    containerId?: string;
    regTestSpec?: string;
    headerText?: string;
    url?: string;
    expandable?: boolean;
    allItemsVisible?: boolean;
    deletableItems?: boolean;
    onDeleteItems?: (items: Array<ContinueWatching>) => void;
    showAccessibilityLinks?: boolean;
    banner?: () => React.ReactNode;
};

const ContentList = (props: Props): JSX.Element => {
    const {
        items,
        location,
        id,
        presentation,
        cardType,
        className,
        preferredAccessVersion,
        onItemClick,
        onItemImpression,
        continueWatchingIsActive,
        isInSweden,
        autoPlay,
        withDivider,
        standalone,
        vtp,
        containerId,
        regTestSpec,
        headerText,
        url,
        expandable,
        allItemsVisible,
        deletableItems,
        onDeleteItems,
        showAccessibilityLinks,
        banner,
    } = props;

    const [expanded, toggleExpanded] = useState(false);
    const [editModeActivated, toggleEditMode] = useState(false);
    const [markedAsDeleted, setMarkedAsDeleted] = useState<Array<ContinueWatching>>([]);

    if (items.length <= 0) {
        return null;
    }

    const NUMBER_OF_ITEMS_LIMIT = 24;

    const changeLayout = () => {
        toggleExpanded(!expanded);
    };

    const onEditDone = () => {
        onDeleteItems(markedAsDeleted);
        toggleEditMode(false);
    };

    const onEditModeActivated = () => {
        toggleEditMode(true);
    };

    const getTotalItems = () => {
        if (items.length > NUMBER_OF_ITEMS_LIMIT && id !== 'favorites') {
            return items.slice(0, NUMBER_OF_ITEMS_LIMIT - 1);
        }

        return items;
    };

    const onMarkedAsDeleted = (deleteItem: ContinueWatching) => {
        let result = markedAsDeleted;

        const index = markedAsDeleted.findIndex((markedItem) => markedItem.videoId === deleteItem.videoId);

        if (index > -1) {
            result = markedAsDeleted.filter((markedItem) => markedItem.videoId !== deleteItem.videoId);
        } else {
            result.push(deleteItem);
        }

        setMarkedAsDeleted(result);
    };

    return (
        <StyledContentList
            withDivider={withDivider}
            standalone={standalone}
            vtp={vtp}
            id={containerId}
            className={className}
            data-rt="slidable-list"
            data-rt-spec={regTestSpec}
            data-helix-type="list"
            aria-label={headerText}
        >
            <Header
                id={id}
                regTestSpec={regTestSpec}
                url={url}
                text={headerText}
                expanded={expanded}
                expandable={expandable}
                allItemsVisible={allItemsVisible}
                changeLayout={changeLayout}
                showEditButtons={deletableItems}
                markedAsDeleted={markedAsDeleted}
                onEditDone={onEditDone}
                onEditModeActivated={onEditModeActivated}
                showAccessibilityLinks={showAccessibilityLinks}
            />
            <VideoList expanded={expanded}>
                <VideoListContainer expanded={expanded}>
                    <>
                        {expanded && (
                            <ContentItemGrid
                                items={getTotalItems()}
                                id={id}
                                editModeActivated={editModeActivated}
                                presentation={presentation}
                                preferredAccessVersion={preferredAccessVersion}
                                onItemClick={onItemClick}
                                onItemImpression={onItemImpression}
                                onMarkedAsDeleted={onMarkedAsDeleted}
                                continueWatchingIsActive={continueWatchingIsActive}
                                autoPlay={autoPlay}
                            />
                        )}
                        {!expanded && (
                            <ContentItemCarousel
                                dataRt="content-item-carousel"
                                items={getTotalItems()}
                                id={id}
                                cardType={cardType}
                                editModeActivated={editModeActivated}
                                location={location}
                                presentation={presentation}
                                preferredAccessVersion={preferredAccessVersion}
                                onItemClick={onItemClick}
                                onItemImpression={onItemImpression}
                                onMarkItemAsDeleted={onMarkedAsDeleted}
                                continueWatchingIsActive={continueWatchingIsActive}
                                markedAsDeleted={markedAsDeleted}
                                isInSweden={isInSweden}
                                autoPlay={autoPlay}
                                banner={banner}
                            />
                        )}
                    </>
                </VideoListContainer>
            </VideoList>
        </StyledContentList>
    );
};

export default ContentList;
